import React, { useCallback, useState } from 'react';
import { BarChart2, Save, RefreshCw, Download, Calculator, PiggyBank } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import LoanParametersSection from './LoanParametersSection';
import CurrentLoanStatusSection from './CurrentLoanStatusSection';
import EarlyRepaymentOptionsSection from './EarlyRepaymentOptionsSection';
import AdditionalFeesSection from './AdditionalFeesSection';

// Импорт утилитарных функций
import { isPositiveNumber, isInteger } from '../../../../../utils/validation';

/**
 * Компонент основной формы расчета досрочного погашения кредита
 */
const EarlyRepaymentForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  originalSchedule,
  setOriginalSchedule,
  newSchedule,
  setNewSchedule,
  loanTypes,
  earlyRepaymentFrequencies,
  earlyRepaymentTypes,
  saveScenario 
}) => {
  
  // Состояние для имени сохраняемого сценария
  const [saveScenarioName, setSaveScenarioName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Функция парсинга целого числа или возврат значения по умолчанию
  const parseIntOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Сбрасываем связанные поля если функция отключена
      if (name === 'includeFees' && !checked) {
        newState.initialFee = '0';
        newState.monthlyFee = '0';
        newState.monthlyFeeType = 'fixed';
        newState.earlyRepaymentFee = '0';
        newState.earlyRepaymentFeeType = 'fixed';
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      loanAmount, loanTerm, interestRate, startDate, currentDate, paymentsMade,
      earlyRepaymentAmount, earlyRepaymentType, earlyRepaymentFrequency, earlyRepaymentStartMonth,
      includeFees, initialFee, monthlyFee, monthlyFeeType, earlyRepaymentFee, earlyRepaymentFeeType, paymentType
    } = formData;

    // Парсинг числовых значений
    const numLoanAmount = parseFloatOrDefault(loanAmount, -1);
    const numLoanTermYears = parseFloatOrDefault(loanTerm, -1);
    const numLoanTermMonths = Math.round(numLoanTermYears * 12);
    const numInterestRate = parseFloatOrDefault(interestRate, -1);
    const numPaymentsMade = parseIntOrDefault(paymentsMade, -1);
    const numEarlyRepaymentAmount = parseFloatOrDefault(earlyRepaymentAmount, -1);
    const numEarlyRepaymentStartMonth = parseIntOrDefault(earlyRepaymentStartMonth, -1);

    // Валидация основных полей кредита
    if (numLoanAmount <= 0) newErrors.loanAmount = 'Сумма должна быть положительной.';
    if (!newErrors.loanAmount && numLoanAmount > 50000000) newErrors.loanAmount = 'Максимальная сумма 50 млн ₽.';
    
    if (numLoanTermYears <= 0) newErrors.loanTerm = 'Срок должен быть положительным.';
    if (!newErrors.loanTerm && numLoanTermYears > 30) newErrors.loanTerm = 'Максимальный срок 30 лет.';
    if (!newErrors.loanTerm && numLoanTermMonths <= 0) newErrors.loanTerm = 'Срок должен быть не менее 1 месяца.';
    
    if (numInterestRate <= 0) newErrors.interestRate = 'Ставка должна быть положительной.';
    if (!newErrors.interestRate && numInterestRate > 100) newErrors.interestRate = 'Ставка не может быть более 100%.';

    // Проверка даты начала кредита
    if (!startDate) newErrors.startDate = 'Укажите дату начала кредита.';
    try {
      const dateObject = new Date(startDate);
      if (isNaN(dateObject.getTime())) {
        newErrors.startDate = 'Некорректная дата.';
      }
    } catch (e) {
      newErrors.startDate = 'Некорректная дата.';
    }

    // Проверка текущей даты
    if (!currentDate) newErrors.currentDate = 'Укажите текущую дату.';
    try {
      const startDateObj = new Date(startDate);
      const currentDateObj = new Date(currentDate);
      
      if (isNaN(currentDateObj.getTime())) {
        newErrors.currentDate = 'Некорректная дата.';
      } else if (currentDateObj < startDateObj) {
        newErrors.currentDate = 'Текущая дата не может быть раньше даты начала кредита.';
      }
    } catch (e) {
      newErrors.currentDate = 'Некорректная дата.';
    }

    // Валидация уже сделанных платежей
    if (!isInteger(paymentsMade) || numPaymentsMade < 0) {
      newErrors.paymentsMade = 'Введите целое неотрицательное число.';
    }
    if (!newErrors.paymentsMade && numPaymentsMade >= numLoanTermMonths) {
      newErrors.paymentsMade = `Значение не может быть больше или равно сроку кредита (${numLoanTermMonths} мес.).`;
    }

    // Валидация суммы досрочного погашения
    if (numEarlyRepaymentAmount <= 0) newErrors.earlyRepaymentAmount = 'Сумма должна быть положительной.';
    if (!newErrors.earlyRepaymentAmount && numEarlyRepaymentAmount >= numLoanAmount) {
      newErrors.earlyRepaymentAmount = 'Сумма досрочного погашения не может быть больше или равна сумме кредита.';
    }

    // Валидация месяца начала досрочного погашения
    if (!isInteger(earlyRepaymentStartMonth) || numEarlyRepaymentStartMonth <= 0) {
      newErrors.earlyRepaymentStartMonth = 'Введите целый положительный номер месяца.';
    }
    if (!newErrors.loanTerm && !newErrors.earlyRepaymentStartMonth && numEarlyRepaymentStartMonth > numLoanTermMonths) {
      newErrors.earlyRepaymentStartMonth = `Начало (${numEarlyRepaymentStartMonth}) не может быть позже срока (${numLoanTermMonths} мес.).`;
    }
    if (!newErrors.earlyRepaymentStartMonth && !newErrors.paymentsMade && numEarlyRepaymentStartMonth <= numPaymentsMade) {
      newErrors.earlyRepaymentStartMonth = `Начало (${numEarlyRepaymentStartMonth}) должно быть позже уже оплаченных месяцев (${numPaymentsMade}).`;
    }

    // Валидация типа и частоты досрочного погашения
    if (!earlyRepaymentType) newErrors.earlyRepaymentType = "Выберите тип досрочного погашения.";
    if (!earlyRepaymentFrequency) newErrors.earlyRepaymentFrequency = "Выберите периодичность.";

    // Валидация комиссий
    if (includeFees) {
      const numInitialFee = parseFloatOrDefault(initialFee, -1);
      const numMonthlyFee = parseFloatOrDefault(monthlyFee, -1);
      const numEarlyRepaymentFee = parseFloatOrDefault(earlyRepaymentFee, -1);
      
      if (numInitialFee < 0) newErrors.initialFee = 'Значение не может быть отрицательным.';
      if (numMonthlyFee < 0) newErrors.monthlyFee = 'Значение не может быть отрицательным.';
      if (numEarlyRepaymentFee < 0) newErrors.earlyRepaymentFee = 'Значение не может быть отрицательным.';
      
      if (monthlyFeeType === 'percent' && numMonthlyFee > 100) {
        newErrors.monthlyFee = 'Процент не может быть больше 100%.';
      }
      
      if (earlyRepaymentFeeType === 'percent' && numEarlyRepaymentFee > 100) {
        newErrors.earlyRepaymentFee = 'Процент не может быть больше 100%.';
      }
    }

    // Валидация типа платежа
    if (!paymentType || !['annuity', 'differentiated'].includes(paymentType)) {
      newErrors.paymentType = "Выберите корректный тип платежа.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Расчет аннуитетного платежа
  const calculateAnnuityPayment = (principal, termMonths, annualRate) => {
    if (principal <= 0 || termMonths <= 0) return 0;
    const monthlyRate = annualRate / 12 / 100;

    if (monthlyRate === 0) {
      return principal / termMonths;
    }

    const factor = Math.pow(1 + monthlyRate, termMonths);
    if (!isFinite(factor) || factor === 1) {
      return principal / termMonths;
    }
    return principal * monthlyRate * factor / (factor - 1);
  };

  // Функция расчета графика платежей
  const calculateSchedules = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);
    setOriginalSchedule([]);
    setNewSchedule([]);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const loanAmountValue = parseFloatOrDefault(formData.loanAmount);
        const initialLoanAmount = loanAmountValue;

        if (initialLoanAmount <= 0) {
          setErrors({ general: "Сумма кредита должна быть положительной." });
          setIsCalculating(false);
          return;
        }

        const initialLoanTermYears = parseFloatOrDefault(formData.loanTerm);
        const initialLoanTermMonths = Math.round(initialLoanTermYears * 12);
        const annualInterestRate = parseFloatOrDefault(formData.interestRate);
        const paymentType = formData.paymentType;
        const startDateObj = new Date(formData.startDate);
        const paymentsMade = parseIntOrDefault(formData.paymentsMade, 0);
        
        // Параметры досрочного погашения
        const earlyRepaymentAmount = parseFloatOrDefault(formData.earlyRepaymentAmount);
        const earlyRepaymentType = formData.earlyRepaymentType;
        const earlyRepaymentFrequency = formData.earlyRepaymentFrequency;
        const earlyRepaymentStartMonth = parseIntOrDefault(formData.earlyRepaymentStartMonth);
        const recalculateAfterEachPayment = formData.recalculateAfterEachPayment;
        
        // Комиссии
        const includeFees = formData.includeFees;
        const initialFee = includeFees ? parseFloatOrDefault(formData.initialFee, 0) : 0;
        const monthlyFee = includeFees ? parseFloatOrDefault(formData.monthlyFee, 0) : 0;
        const monthlyFeeType = formData.monthlyFeeType;
        const earlyRepaymentFee = includeFees ? parseFloatOrDefault(formData.earlyRepaymentFee, 0) : 0;
        const earlyRepaymentFeeType = formData.earlyRepaymentFeeType;

        // --- РАСЧЕТ ОРИГИНАЛЬНОГО ГРАФИКА ПЛАТЕЖЕЙ ---
        const monthlyRate = annualInterestRate / 12 / 100;
        let fixedAnnuityPayment = 0;
        if (paymentType === 'annuity') {
          fixedAnnuityPayment = calculateAnnuityPayment(initialLoanAmount, initialLoanTermMonths, annualInterestRate);
        }
        
        let originalBalance = initialLoanAmount;
        const originalCalculatedSchedule = [];
        let originalTotalInterest = 0;
        let originalTotalPayment = 0;
        
        // Создаем исходный график платежей
        for (let month = 1; month <= initialLoanTermMonths; month++) {
          const paymentDate = new Date(startDateObj);
          paymentDate.setMonth(startDateObj.getMonth() + month - 1);
          
          // Расчет процентов за месяц
          const interestPayment = originalBalance * monthlyRate;
          
          // Расчет основного платежа в зависимости от типа
          let principalPayment = 0;
          let totalMonthlyPayment = 0;
          
          if (paymentType === 'annuity') {
            // Для аннуитетного платежа
            totalMonthlyPayment = fixedAnnuityPayment;
            principalPayment = totalMonthlyPayment - interestPayment;
            
            // Коррекция для последнего платежа
            if (originalBalance < principalPayment) {
              principalPayment = originalBalance;
              totalMonthlyPayment = principalPayment + interestPayment;
            }
          } else {
            // Для дифференцированного платежа
            principalPayment = initialLoanAmount / initialLoanTermMonths;
            totalMonthlyPayment = principalPayment + interestPayment;
          }
          
          // Расчет комиссии за обслуживание
          let monthlyFeePayment = 0;
          if (includeFees && monthlyFee > 0) {
            if (monthlyFeeType === 'percent') {
              monthlyFeePayment = originalBalance * (monthlyFee / 100) / 12;
            } else {
              monthlyFeePayment = monthlyFee;
            }
          }
          
          // Обновление баланса
          const startingBalance = originalBalance;
          originalBalance -= principalPayment;
          originalBalance = Math.max(0, originalBalance);
          
          // Обновление итоговых сумм
          originalTotalInterest += interestPayment;
          originalTotalPayment += totalMonthlyPayment + monthlyFeePayment;
          
          // Добавление в график платежей
          originalCalculatedSchedule.push({
            month,
            date: paymentDate,
            payment: totalMonthlyPayment,
            principalPayment,
            interestPayment,
            monthlyFee: monthlyFeePayment,
            startingBalance,
            remainingBalance: originalBalance,
            isCompleted: month <= paymentsMade
          });
        }
        
        // --- РАСЧЕТ НОВОГО ГРАФИКА ПЛАТЕЖЕЙ С ДОСРОЧНЫМ ПОГАШЕНИЕМ ---
        
        // Определяем частоту досрочного погашения
        let earlyRepaymentFrequencyMonths = 0;
        switch (earlyRepaymentFrequency) {
          case 'monthly': earlyRepaymentFrequencyMonths = 1; break;
          case 'quarterly': earlyRepaymentFrequencyMonths = 3; break;
          case 'semi_annually': earlyRepaymentFrequencyMonths = 6; break;
          case 'annually': earlyRepaymentFrequencyMonths = 12; break;
          case 'once': earlyRepaymentFrequencyMonths = -1; break;
          default: earlyRepaymentFrequencyMonths = 0;
        }
        
        // Копируем уже совершенные платежи в новый график
        const newCalculatedSchedule = [];
        let newBalance = initialLoanAmount;
        let newTotalInterest = 0;
        let newTotalPayment = 0;
        let totalEarlyRepayment = 0;
        let totalEarlyRepaymentFees = 0;
        
        // Копируем уже сделанные платежи из исходного графика
        for (let month = 1; month <= paymentsMade; month++) {
          const payment = originalCalculatedSchedule[month - 1];
          newCalculatedSchedule.push({ ...payment });
          newBalance = payment.remainingBalance;
          newTotalInterest += payment.interestPayment;
          newTotalPayment += payment.payment + payment.monthlyFee;
        }
        
        // Пересчитываем аннуитетный платеж для оставшегося срока и суммы, если необходимо
        let newFixedAnnuityPayment = fixedAnnuityPayment;
        let remainingTermMonths = initialLoanTermMonths - paymentsMade;
        
        if (paymentType === 'annuity') {
          newFixedAnnuityPayment = calculateAnnuityPayment(newBalance, remainingTermMonths, annualInterestRate);
        }
        
        // Расчет оставшихся платежей с учетом досрочного погашения
        let currentMonth = paymentsMade;
        const maxIterations = initialLoanTermMonths * 2; // Для предотвращения бесконечного цикла
        
        while (newBalance > 0.01 && currentMonth < maxIterations) {
          currentMonth++;
          const paymentDate = new Date(startDateObj);
          paymentDate.setMonth(startDateObj.getMonth() + currentMonth - 1);
          
          // Расчет процентов
          const interestPayment = newBalance * monthlyRate;
          
          // Определение регулярного платежа
          let principalPayment = 0;
          let totalMonthlyPayment = 0;
          
          if (paymentType === 'annuity') {
            // Аннуитетный платеж
            totalMonthlyPayment = newFixedAnnuityPayment;
            principalPayment = totalMonthlyPayment - interestPayment;
            
            // Коррекция для последнего платежа
            if (newBalance < principalPayment) {
              principalPayment = newBalance;
              totalMonthlyPayment = principalPayment + interestPayment;
            }
          } else {
            // Дифференцированный платеж
            principalPayment = initialLoanAmount / initialLoanTermMonths;
            totalMonthlyPayment = principalPayment + interestPayment;
          }
          
          // Расчет ежемесячной комиссии
          let monthlyFeePayment = 0;
          if (includeFees && monthlyFee > 0) {
            if (monthlyFeeType === 'percent') {
              monthlyFeePayment = newBalance * (monthlyFee / 100) / 12;
            } else {
              monthlyFeePayment = monthlyFee;
            }
          }
          
          // Определение досрочного платежа
          let earlyRepayment = 0;
          let earlyRepaymentFeePayment = 0;
          
          if (currentMonth >= earlyRepaymentStartMonth && newBalance > 0.01) {
            const isEarlyRepaymentMonth = 
              (earlyRepaymentFrequencyMonths === -1 && currentMonth === earlyRepaymentStartMonth) || // разовое
              (earlyRepaymentFrequencyMonths > 0 && (currentMonth - earlyRepaymentStartMonth) % earlyRepaymentFrequencyMonths === 0); // регулярное
            
            if (isEarlyRepaymentMonth) {
              // Максимальная сумма для досрочного погашения
              const maxEarlyRepayment = newBalance - principalPayment > 0 ? newBalance - principalPayment : 0;
              earlyRepayment = Math.min(earlyRepaymentAmount, maxEarlyRepayment);
              
              // Комиссия за досрочное погашение
              if (includeFees && earlyRepaymentFee > 0) {
                if (earlyRepaymentFeeType === 'percent') {
                  earlyRepaymentFeePayment = earlyRepayment * (earlyRepaymentFee / 100);
                } else {
                  earlyRepaymentFeePayment = earlyRepaymentFee;
                }
              }
              
              totalEarlyRepayment += earlyRepayment;
              totalEarlyRepaymentFees += earlyRepaymentFeePayment;
            }
          }
          
          // Обновление баланса
          const startingBalance = newBalance;
          newBalance -= (principalPayment + earlyRepayment);
          newBalance = Math.max(0, newBalance);
          
          // Обновление итоговых сумм
          newTotalInterest += interestPayment;
          newTotalPayment += totalMonthlyPayment + monthlyFeePayment + earlyRepaymentFeePayment;
          
          // Добавление в новый график платежей
          newCalculatedSchedule.push({
            month: currentMonth,
            date: paymentDate,
            payment: totalMonthlyPayment,
            principalPayment,
            interestPayment,
            monthlyFee: monthlyFeePayment,
            earlyRepayment,
            earlyRepaymentFee: earlyRepaymentFeePayment,
            totalPayment: totalMonthlyPayment + monthlyFeePayment + earlyRepayment + earlyRepaymentFeePayment,
            startingBalance,
            remainingBalance: newBalance,
            isCompleted: false
          });
          
          // Пересчет аннуитетного платежа после досрочного погашения
          if (earlyRepayment > 0 && paymentType === 'annuity' && newBalance > 0.01) {
            remainingTermMonths = initialLoanTermMonths - currentMonth;
            
            if (remainingTermMonths > 0 && recalculateAfterEachPayment) {
              if (earlyRepaymentType === 'payment') {
                // Уменьшение платежа при сохранении срока
                newFixedAnnuityPayment = calculateAnnuityPayment(newBalance, remainingTermMonths, annualInterestRate);
              }
              // При уменьшении срока платеж остается прежним
            }
          }
        }
        
        // Проверка на слишком большое количество итераций
        if (currentMonth >= maxIterations && newBalance > 0.01) {
          setErrors({ general: "Расчет прерван из-за превышения лимита итераций. Проверьте параметры кредита и досрочного погашения." });
          setResults(null);
          setOriginalSchedule([]);
          setNewSchedule([]);
          setIsCalculating(false);
          return;
        }
        
        // --- ПОДГОТОВКА РЕЗУЛЬТАТОВ ---
        
        // Общие результаты
        const savedMonths = initialLoanTermMonths - newCalculatedSchedule.length;
        const savedInterest = originalTotalInterest - newTotalInterest;
        const savedTotal = originalTotalPayment - (newTotalPayment + totalEarlyRepayment);
        
        const finalResults = {
          // Исходные параметры
          loanAmount: initialLoanAmount,
          loanTermYears: initialLoanTermYears,
          loanTermMonths: initialLoanTermMonths,
          interestRate: annualInterestRate,
          paymentType: paymentType,
          paymentsMade: paymentsMade,
          
          // Результаты оригинального графика
          originalMonthlyPayment: originalCalculatedSchedule[0].payment,
          originalLastPaymentDate: originalCalculatedSchedule[initialLoanTermMonths - 1].date,
          originalTotalInterest: originalTotalInterest,
          originalTotalPayment: originalTotalPayment,
          
          // Результаты после досрочного погашения
          newTermMonths: newCalculatedSchedule.length,
          newMonthlyPayment: newCalculatedSchedule[paymentsMade].payment,
          newLastPaymentDate: newCalculatedSchedule[newCalculatedSchedule.length - 1].date,
          newTotalInterest: newTotalInterest,
          newTotalPayment: newTotalPayment + totalEarlyRepayment,
          
          // Параметры досрочного погашения
          earlyRepaymentAmount: earlyRepaymentAmount,
          earlyRepaymentType: earlyRepaymentType,
          earlyRepaymentFrequency: earlyRepaymentFrequency,
          totalEarlyRepayment: totalEarlyRepayment,
          totalEarlyRepaymentFees: totalEarlyRepaymentFees,
          
          // Экономия от досрочного погашения
          savedMonths: savedMonths,
          savedInterest: savedInterest,
          savedTotal: savedTotal,
          
          // Общие итоги
          effectiveInterestSavings: savedInterest / totalEarlyRepayment * 100 // Эффективность в процентах
        };
        
        // Установка результатов и графиков
        setResults(finalResults);
        setOriginalSchedule(originalCalculatedSchedule);
        setNewSchedule(newCalculatedSchedule);
        
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
        setOriginalSchedule([]);
        setNewSchedule([]);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setOriginalSchedule, setNewSchedule, setIsCalculating]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      loanAmount: '1000000',
      loanTerm: '5',
      interestRate: '10.5',
      paymentType: 'annuity',
      startDate: new Date().toISOString().split('T')[0],
      currentDate: new Date().toISOString().split('T')[0],
      paymentsMade: '12',
      earlyRepaymentAmount: '200000',
      earlyRepaymentType: 'reduction',
      earlyRepaymentFrequency: 'once',
      earlyRepaymentStartMonth: '13',
      recalculateAfterEachPayment: true,
      includeFees: false,
      initialFee: '0',
      monthlyFee: '0',
      monthlyFeeType: 'fixed',
      earlyRepaymentFee: '0',
      earlyRepaymentFeeType: 'fixed',
    });
    setResults(null);
    setOriginalSchedule([]);
    setNewSchedule([]);
    setErrors({});
    setSaveScenarioName('');
    setShowSaveDialog(false);
  };

  // Экспорт графика платежей в CSV
  const exportToCSV = () => {
    if (!newSchedule || newSchedule.length === 0 || !results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Определяем заголовки на основе расчетов
    const headers = ['№', 'Дата', 'Платеж (Осн. долг + %)', 'Основной долг', 'Проценты'];
    
    if (results.totalEarlyRepayment > 0) headers.push('Досрочное погашение');
    if (results.totalEarlyRepaymentFees > 0) headers.push('Комиссия досрочного погашения');
    
    headers.push('Общий платеж за месяц', 'Остаток долга', 'Статус');

    const formatCsvNumber = (num) => num.toFixed(2).replace('.', ',');

    const rows = newSchedule.map(item => {
      const rowData = [
        item.month,
        item.date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }),
        formatCsvNumber(item.payment),
        formatCsvNumber(item.principalPayment),
        formatCsvNumber(item.interestPayment),
      ];
      
      if (results.totalEarlyRepayment > 0) {
        rowData.push(formatCsvNumber(item.earlyRepayment || 0));
      }
      
      if (results.totalEarlyRepaymentFees > 0) {
        rowData.push(formatCsvNumber(item.earlyRepaymentFee || 0));
      }
      
      rowData.push(
        formatCsvNumber(item.totalPayment || (item.payment + (item.earlyRepayment || 0) + (item.earlyRepaymentFee || 0))),
        formatCsvNumber(item.remainingBalance),
        item.isCompleted ? 'Оплачен' : 'Планируется'
      );
      
      return rowData.join(';');
    });

    // Добавим итоговую строку
    const totalRow = [
      '', 'ИТОГО:', 
      formatCsvNumber(newSchedule.reduce((sum, item) => sum + item.payment, 0)),
      formatCsvNumber(formData.loanAmount),
      formatCsvNumber(results.newTotalInterest)
    ];
    
    if (results.totalEarlyRepayment > 0) {
      totalRow.push(formatCsvNumber(results.totalEarlyRepayment));
    }
    
    if (results.totalEarlyRepaymentFees > 0) {
      totalRow.push(formatCsvNumber(results.totalEarlyRepaymentFees));
    }
    
    totalRow.push(
      formatCsvNumber(results.newTotalPayment),
      '0,00',
      ''
    );
    
    // Добавим основную информацию в начало файла
    const infoRows = [
      ['Калькулятор досрочного погашения кредита - Результаты расчета'],
      [''],
      ['Сумма кредита:', formatCsvNumber(results.loanAmount)],
      ['Срок кредита:', `${results.loanTermMonths} мес.`],
      ['Процентная ставка:', `${results.interestRate}%`],
      ['Тип платежа:', formData.paymentType === 'annuity' ? 'Аннуитетный' : 'Дифференцированный'],
      ['Дата начала кредита:', new Date(formData.startDate).toLocaleDateString('ru-RU')],
      ['Выполнено платежей:', formData.paymentsMade],
      [''],
      ['Сумма досрочного погашения:', formatCsvNumber(results.earlyRepaymentAmount)],
      ['Тип погашения:', results.earlyRepaymentType === 'reduction' ? 'Уменьшение срока' : 'Уменьшение платежа'],
      ['Периодичность:', getFrequencyName(results.earlyRepaymentFrequency)],
      [''],
      ['Экономия на процентах:', formatCsvNumber(results.savedInterest)],
      ['Сокращение срока:', `${results.savedMonths} мес.`],
      ['Эффективность погашения:', `${formatCsvNumber(results.effectiveInterestSavings)}%`],
      [''],  // Пустая строка для разделения
    ].map(row => row.join(';'));

    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + [...infoRows, headers.join(';'), ...rows, totalRow.join(';')].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'график_досрочного_погашения.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  
  // Получение названия частоты досрочного погашения
  const getFrequencyName = (frequency) => {
    const freq = earlyRepaymentFrequencies.find(f => f.id === frequency);
    return freq ? freq.name : frequency;
  };

  // Обработчик сохранения сценария
  const handleSaveScenario = () => {
    if (showSaveDialog) {
      const savedResult = saveScenario(saveScenarioName || null);
      if (savedResult) {
        alert(`Сценарий досрочного погашения "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveScenarioName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateSchedules();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <BarChart2 className="w-6 h-6 text-green-500" />
          Параметры расчета досрочного погашения кредита
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Основные параметры кредита */}
        <LoanParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Текущий статус кредита */}
        <CurrentLoanStatusSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Параметры досрочного погашения */}
        <EarlyRepaymentOptionsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          earlyRepaymentFrequencies={earlyRepaymentFrequencies}
          earlyRepaymentTypes={earlyRepaymentTypes}
        />

        {/* Дополнительные комиссии */}
        <AdditionalFeesSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Диалог сохранения сценария */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveScenarioName}
                onChange={(e) => setSaveScenarioName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveScenario}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<Calculator className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать досрочное погашение'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {newSchedule.length > 0 && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать график (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveScenario}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить сценарий
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default EarlyRepaymentForm;